import { EvidenceAnchor, Metric } from './schemas/cig'

type LegacyMetrics =
  | Metric[]
  | Record<string, unknown>
  | undefined
  | null

const DEFAULT_CONFIDENCE: Metric['confidence'] = 'medium'

const titleCase = (value: string): string =>
  value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

const normalizeAnchors = (anchors: unknown): EvidenceAnchor[] => {
  if (!Array.isArray(anchors)) return []
  return anchors
    .filter((anchor): anchor is Record<string, unknown> => !!anchor && typeof anchor === 'object')
    .map((anchor) => ({
      source_id: (anchor.source_id as string) ?? '',
      source_url: (anchor.source_url as string) ?? (anchor.url as string) ?? undefined,
      locator: (anchor.locator as string) ?? (anchor.location as string) ?? '',
      snippet: (anchor.snippet as string) ?? (anchor.text as string) ?? '',
      support_type: (anchor.support_type as EvidenceAnchor['support_type']) ?? 'supports',
      reliability_score: anchor.reliability_score as number | undefined,
      timestamp: anchor.timestamp as string | undefined,
    }))
}

const coerceMetricValue = (value: unknown): Metric['value'] => {
  if (typeof value === 'number' || typeof value === 'string') {
    return value
  }
  if (value && typeof value === 'object') {
    const objectValue = (value as Record<string, unknown>).value
    const formatted = (value as Record<string, unknown>).formatted
    if (typeof objectValue === 'number') {
      return formatted && typeof formatted === 'string'
        ? { value: objectValue, formatted }
        : { value: objectValue }
    }
    if (typeof objectValue === 'string') {
      return objectValue
    }
  }
  if (value == null) {
    return ''
  }
  return String(value)
}

export const normalizeLegacyMetrics = (metricsInput: LegacyMetrics): Metric[] => {
  if (Array.isArray(metricsInput)) {
    return metricsInput
  }
  if (!metricsInput || typeof metricsInput !== 'object') {
    return []
  }

  return Object.entries(metricsInput as Record<string, unknown>).map(([metricId, rawMetric]) => {
    const metricObject = (rawMetric && typeof rawMetric === 'object' ? rawMetric : { value: rawMetric }) as Record<
      string,
      unknown
    >

    const metricValue = coerceMetricValue('value' in metricObject ? metricObject.value : rawMetric)

    const metric: Metric = {
      id: (metricObject.id as string) ?? metricId,
      label: (metricObject.label as string) ?? titleCase(metricId),
      value: metricValue,
      unit: metricObject.unit as string | undefined,
      timeframe: metricObject.timeframe as string | undefined,
      trend: metricObject.trend as Metric['trend'],
      trend_value: metricObject.trend_value as number | undefined,
      confidence: (metricObject.confidence as Metric['confidence']) ?? DEFAULT_CONFIDENCE,
      evidence_anchors: normalizeAnchors(metricObject.evidence_anchors ?? metricObject.anchors),
      scenario_binding: metricObject.scenario_binding as Metric['scenario_binding'],
      category: metricObject.category as string | undefined,
    }

    return metric
  })
}

